/**
 * Utilities for auth redirect flow when token expires.
 * Saves current page URL so user returns there after successful login.
 */

const RETURN_URL_KEY = 'auth.returnUrl';

/**
 * Check if a path is an auth page (login, register, etc.)
 */
function isAuthPath(pathname: string): boolean {
  return (
    pathname.includes('/login') ||
    pathname.includes('/register') ||
    pathname.includes('/forgot-password') ||
    pathname.includes('/reset-password')
  );
}

/**
 * Save the current page URL to return to after login.
 * Call this when 401 occurs (token expired).
 */
export function saveReturnUrl(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  if (isAuthPath(pathname)) return;
  const returnUrl = pathname + search;
  try {
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
  } catch {
    // Ignore quota errors
  }
}

/**
 * Get the saved return URL and clear it.
 * Returns null if none or invalid.
 */
export function popReturnUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    if (!url) return null;
    // Security: only allow relative paths (same origin)
    if (url.startsWith('http://') || url.startsWith('https://')) return null;
    if (!url.startsWith('/')) return null;
    if (isAuthPath(url)) return null;
    return url;
  } catch {
    return null;
  }
}

/**
 * Get the path to redirect to after successful login.
 * Returns saved returnUrl if valid, otherwise null (caller uses default).
 */
export function getRedirectPathAfterLogin(defaultPath: string): string {
  const returnUrl = popReturnUrl();
  if (returnUrl) return returnUrl;
  return defaultPath;
}

/**
 * Redirect to login page, saving current URL for return.
 * For admin pages, redirects to admin login.
 */
export function redirectToLoginOnTokenExpired(): void {
  if (typeof window === 'undefined') return;
  saveReturnUrl();
  const { pathname } = window.location;
  const isAdminPage = pathname.includes('/admin/');
  const locale = pathname.split('/')[1] || 'ar';
  const loginPath = isAdminPage ? `/${locale}/login` : `/${locale}/login`;
  window.location.replace(loginPath);
}
